// Drawing App - Brush Engine (Dab-based rendering)

import { Point, BrushSettings, BrushPresetType } from './types';
import { TextureManager } from './texture_manager';

export class BrushEngine {
  private dabCanvas: OffscreenCanvas;
  private dabCtx: OffscreenCanvasRenderingContext2D;
  private strokeCanvas: OffscreenCanvas | null = null;
  private strokeCtx: OffscreenCanvasRenderingContext2D | null = null;
  private lastDabPos: { x: number; y: number } | null = null;
  private currentPreset: BrushPresetType = 'default';
  private strokePoints: { x: number; y: number; pressure: number }[] = [];
  private noiseBuffer: Float32Array;
  private textureManager: TextureManager | null = null;
  private customBrushBitmap: ImageBitmap | null = null;

  constructor() {
    this.dabCanvas = new OffscreenCanvas(256, 256);
    this.dabCtx = this.dabCanvas.getContext('2d')!;
    // Pre-generate noise for texture brushes
    this.noiseBuffer = new Float32Array(256 * 256);
    for (let i = 0; i < this.noiseBuffer.length; i++) {
      this.noiseBuffer[i] = Math.random();
    }
  }

  setTextureManager(manager: TextureManager): void {
    this.textureManager = manager;
  }

  async setCustomBrush(dataURL: string | null): Promise<void> {
    if (this.customBrushBitmap) {
      this.customBrushBitmap.close();
      this.customBrushBitmap = null;
    }
    if (!dataURL) return;

    const response = await fetch(dataURL);
    const blob = await response.blob();
    this.customBrushBitmap = await createImageBitmap(blob);
  }

  getCustomBrushBitmap(): ImageBitmap | null {
    return this.customBrushBitmap;
  }

  setPreset(preset: BrushPresetType): void {
    this.currentPreset = preset;
  }

  getPreset(): BrushPresetType {
    return this.currentPreset;
  }

  getStrokePoints(): { x: number; y: number; pressure: number }[] {
    return this.strokePoints;
  }

  beginStroke(canvasWidth: number, canvasHeight: number): void {
    this.strokeCanvas = new OffscreenCanvas(canvasWidth, canvasHeight);
    this.strokeCtx = this.strokeCanvas.getContext('2d')!;
    this.lastDabPos = null;
    this.strokePoints = [];
  }

  addPoint(point: Point, brush: BrushSettings, color: string): void {
    if (!this.strokeCtx) return;

    this.strokePoints.push({ x: point.x, y: point.y, pressure: point.pressure });

    const pressureSize = this.mapPressure(point.pressure, 0.3, 1.0) * brush.size;
    const pressureOpacity = this.mapPressure(point.pressure, 0.1, 1.0) * brush.flow;

    if (!this.lastDabPos) {
      this.renderDab(point.x, point.y, pressureSize, pressureOpacity, brush, color, point);
      this.lastDabPos = { x: point.x, y: point.y };
      return;
    }

    // Interpolate dabs along the stroke path
    const dx = point.x - this.lastDabPos.x;
    const dy = point.y - this.lastDabPos.y;
    const distance = Math.hypot(dx, dy);
    const spacing = Math.max(1, pressureSize * brush.spacing);

    if (distance < spacing) return;

    const steps = Math.ceil(distance / spacing);
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      const x = this.lastDabPos.x + dx * t;
      const y = this.lastDabPos.y + dy * t;

      // Apply jitter
      const jitterX = (Math.random() - 0.5) * brush.jitter * pressureSize;
      const jitterY = (Math.random() - 0.5) * brush.jitter * pressureSize;

      // Size jitter
      const sizeJitter = 1 + (Math.random() - 0.5) * brush.jitter * 0.5;
      const dabSize = pressureSize * sizeJitter;

      this.renderDab(x + jitterX, y + jitterY, dabSize, pressureOpacity, brush, color, point);
    }

    this.lastDabPos = { x: point.x, y: point.y };
  }

  endStroke(targetCtx: OffscreenCanvasRenderingContext2D, opacity: number, compositeOp: GlobalCompositeOperation): void {
    if (!this.strokeCanvas) return;

    targetCtx.save();
    targetCtx.globalAlpha = opacity;
    targetCtx.globalCompositeOperation = compositeOp;
    targetCtx.drawImage(this.strokeCanvas, 0, 0);
    targetCtx.restore();

    this.strokeCanvas = null;
    this.strokeCtx = null;
    this.lastDabPos = null;
  }

  getStrokeCanvas(): OffscreenCanvas | null {
    return this.strokeCanvas;
  }

  private renderDab(x: number, y: number, size: number, opacity: number, brush: BrushSettings, color: string, point?: Point): void {
    if (!this.strokeCtx) return;

    const radius = size / 2;
    if (radius < 0.5) return;

    // If custom brush is active, use stamp rendering
    if (this.customBrushBitmap && brush.customBrushImage) {
      this.renderCustomDab(x, y, size, opacity, brush, color);
      return;
    }

    // Determine if we need texture post-processing
    const needsTexture = brush.textureType && brush.textureType !== 'none' && brush.textureStrength > 0 && this.textureManager;

    if (needsTexture) {
      // Render dab to local dabCanvas, apply texture, then stamp to strokeCanvas
      const dabSize = Math.ceil(size + 4);
      if (this.dabCanvas.width < dabSize || this.dabCanvas.height < dabSize) {
        this.dabCanvas = new OffscreenCanvas(dabSize, dabSize);
        this.dabCtx = this.dabCanvas.getContext('2d')!;
      }
      this.dabCtx.clearRect(0, 0, dabSize, dabSize);

      // Render shape at local center
      const localCenter = dabSize / 2;
      this.renderDabShape(this.dabCtx, localCenter, localCenter, radius, opacity, brush, color, point);

      // Apply texture
      this.applyTexture(this.dabCtx, dabSize, dabSize, brush, x, y);

      // Stamp onto stroke canvas
      this.strokeCtx.drawImage(this.dabCanvas, 0, 0, dabSize, dabSize, x - dabSize / 2, y - dabSize / 2, dabSize, dabSize);
    } else {
      // Direct rendering (original path, no texture)
      this.renderDabShape(this.strokeCtx, x, y, radius, opacity, brush, color, point);
    }
  }

  private renderDabShape(ctx: OffscreenCanvasRenderingContext2D, x: number, y: number, radius: number, opacity: number, brush: BrushSettings, color: string, point?: Point): void {
    switch (this.currentPreset) {
      case 'watercolor':
        this.renderWatercolorDab(ctx, x, y, radius, opacity, brush, color);
        break;
      case 'calligraphy':
        this.renderCalligraphyDab(ctx, x, y, radius, opacity, brush, color, point);
        break;
      case 'crayon':
        this.renderCrayonDab(ctx, x, y, radius, opacity, brush, color);
        break;
      default:
        this.renderDefaultDab(ctx, x, y, radius, opacity, brush, color);
        break;
    }
  }

  private renderCustomDab(x: number, y: number, size: number, opacity: number, _brush: BrushSettings, color: string): void {
    if (!this.strokeCtx || !this.customBrushBitmap) return;

    const dabSize = Math.ceil(size + 4);
    if (this.dabCanvas.width < dabSize || this.dabCanvas.height < dabSize) {
      this.dabCanvas = new OffscreenCanvas(dabSize, dabSize);
      this.dabCtx = this.dabCanvas.getContext('2d')!;
    }
    this.dabCtx.clearRect(0, 0, dabSize, dabSize);

    // Draw custom brush stamp scaled to dab size
    this.dabCtx.drawImage(this.customBrushBitmap, 0, 0, dabSize, dabSize);

    // Tint with color using source-in composite
    this.dabCtx.globalCompositeOperation = 'source-in';
    this.dabCtx.fillStyle = color;
    this.dabCtx.globalAlpha = opacity;
    this.dabCtx.fillRect(0, 0, dabSize, dabSize);
    this.dabCtx.globalCompositeOperation = 'source-over';
    this.dabCtx.globalAlpha = 1.0;

    // Stamp onto stroke canvas
    this.strokeCtx.drawImage(this.dabCanvas, 0, 0, dabSize, dabSize, x - dabSize / 2, y - dabSize / 2, dabSize, dabSize);
  }

  private applyTexture(ctx: OffscreenCanvasRenderingContext2D, width: number, height: number, brush: BrushSettings, worldX: number, worldY: number): void {
    if (!this.textureManager || !brush.textureType || brush.textureType === 'none') return;

    const texture = this.textureManager.getTexture(brush.textureType);
    if (!texture) return;

    const strength = brush.textureStrength;

    // Create temporary canvas for texture sampling
    const tempCanvas = new OffscreenCanvas(width, height);
    const tempCtx = tempCanvas.getContext('2d')!;

    // Tile the texture using world coordinates for consistent tiling
    const texW = texture.width;
    const texH = texture.height;
    const offsetX = ((worldX % texW) + texW) % texW;
    const offsetY = ((worldY % texH) + texH) % texH;

    // Draw tiled texture
    for (let ty = -1; ty <= 1; ty++) {
      for (let tx = -1; tx <= 1; tx++) {
        tempCtx.drawImage(texture, tx * texW - offsetX, ty * texH - offsetY);
      }
    }

    // Modulate: use destination-in to mask the dab by texture luminance
    // First, blend original with textured version based on strength
    if (strength < 1.0) {
      // Partial strength: save original, apply texture, blend back
      const origData = ctx.getImageData(0, 0, width, height);
      const texData = tempCtx.getImageData(0, 0, width, height);

      for (let i = 0; i < origData.data.length; i += 4) {
        // Use texture luminance to modulate alpha
        const texLum = texData.data[i] / 255; // grayscale texture
        const alphaFactor = 1.0 - strength * (1.0 - texLum);
        origData.data[i + 3] = Math.round(origData.data[i + 3] * alphaFactor);
      }

      ctx.putImageData(origData, 0, 0);
    } else {
      // Full strength
      const origData = ctx.getImageData(0, 0, width, height);
      const texData = tempCtx.getImageData(0, 0, width, height);

      for (let i = 0; i < origData.data.length; i += 4) {
        const texLum = texData.data[i] / 255;
        origData.data[i + 3] = Math.round(origData.data[i + 3] * texLum);
      }

      ctx.putImageData(origData, 0, 0);
    }
  }

  private renderDefaultDab(ctx: OffscreenCanvasRenderingContext2D, x: number, y: number, radius: number, opacity: number, brush: BrushSettings, color: string): void {
    ctx.save();

    if (brush.hardness >= 0.95) {
      ctx.globalAlpha = opacity;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    } else {
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
      const innerRadius = brush.hardness;
      gradient.addColorStop(0, color);
      gradient.addColorStop(innerRadius, color);
      gradient.addColorStop(1, this.colorWithAlpha(color, 0));

      ctx.globalAlpha = opacity;
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  private renderWatercolorDab(ctx: OffscreenCanvasRenderingContext2D, x: number, y: number, radius: number, opacity: number, _brush: BrushSettings, color: string): void {
    ctx.save();

    // Irregular edge with noise
    const wobbleRadius = radius * (0.85 + Math.random() * 0.3);
    const gradient = ctx.createRadialGradient(x, y, 0, x, y, wobbleRadius);
    const baseOpacity = opacity * (0.3 + Math.random() * 0.4);

    gradient.addColorStop(0, this.colorWithAlpha(color, 0.6));
    gradient.addColorStop(0.4, this.colorWithAlpha(color, 0.4));
    gradient.addColorStop(0.7, this.colorWithAlpha(color, 0.2));
    gradient.addColorStop(1, this.colorWithAlpha(color, 0));

    ctx.globalAlpha = baseOpacity;
    ctx.fillStyle = gradient;

    // Draw slightly irregular circle
    ctx.beginPath();
    const segments = 12;
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      const noiseIdx = Math.floor((x + y + i * 37) % 256);
      const noise = this.noiseBuffer[noiseIdx * 256 % this.noiseBuffer.length];
      const r = wobbleRadius * (0.9 + noise * 0.2);
      const px = x + Math.cos(angle) * r;
      const py = y + Math.sin(angle) * r;
      if (i === 0) ctx.moveTo(px, py);
      else ctx.lineTo(px, py);
    }
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  }

  private renderCalligraphyDab(ctx: OffscreenCanvasRenderingContext2D, x: number, y: number, radius: number, opacity: number, _brush: BrushSettings, color: string, point?: Point): void {
    ctx.save();

    // Use tilt for elliptical dab
    const tiltX = point?.tiltX || 0;
    const tiltY = point?.tiltY || 0;
    const angle = Math.atan2(tiltY, tiltX || 1);
    const tiltMagnitude = Math.min(1, Math.hypot(tiltX, tiltY) / 45);

    // Ellipse ratio based on tilt
    const scaleX = 1.0;
    const scaleY = 0.3 + (1 - tiltMagnitude) * 0.7;

    ctx.globalAlpha = opacity;
    ctx.fillStyle = color;
    ctx.translate(x, y);
    ctx.rotate(angle + Math.PI / 4); // 45-degree default angle for calligraphy
    ctx.beginPath();
    ctx.ellipse(0, 0, radius * scaleX, radius * scaleY, 0, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  private renderCrayonDab(ctx: OffscreenCanvasRenderingContext2D, x: number, y: number, radius: number, opacity: number, _brush: BrushSettings, color: string): void {
    ctx.save();

    ctx.globalAlpha = opacity * 0.8;

    // Create texture by drawing multiple small dots with gaps
    const dotCount = Math.max(5, Math.floor(radius * radius * 0.3));
    ctx.fillStyle = color;

    for (let i = 0; i < dotCount; i++) {
      const noiseIdx = Math.floor((x * 7 + y * 13 + i * 31) % this.noiseBuffer.length);
      const n1 = this.noiseBuffer[noiseIdx];
      const n2 = this.noiseBuffer[(noiseIdx + 1) % this.noiseBuffer.length];

      // Random position within the radius
      const angle = n1 * Math.PI * 2;
      const dist = n2 * radius;
      const px = x + Math.cos(angle) * dist;
      const py = y + Math.sin(angle) * dist;

      // Skip some dots to create texture gaps
      if (this.noiseBuffer[(noiseIdx + 2) % this.noiseBuffer.length] > 0.6) continue;

      const dotRadius = 0.5 + n1 * 1.5;
      ctx.beginPath();
      ctx.arc(px, py, dotRadius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  private mapPressure(pressure: number, min: number, max: number): number {
    return min + (max - min) * Math.max(0, Math.min(1, pressure));
  }

  private colorWithAlpha(color: string, alpha: number): string {
    // Parse hex color and add alpha
    if (color.startsWith('#')) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r},${g},${b},${alpha})`;
    }
    return color;
  }

  // Create eraser-style rendering
  renderEraserDab(ctx: OffscreenCanvasRenderingContext2D, x: number, y: number, size: number, pressure: number, hardness: number): void {
    const radius = (size / 2) * this.mapPressure(pressure, 0.3, 1.0);
    if (radius < 0.5) return;

    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.globalAlpha = this.mapPressure(pressure, 0.1, 1.0);

    if (hardness >= 0.95) {
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    } else {
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
      gradient.addColorStop(0, 'rgba(0,0,0,1)');
      gradient.addColorStop(hardness, 'rgba(0,0,0,1)');
      gradient.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }
}
